/**
 * AuditRuleCard
 *
 * Redesigned JotAudit compliance card for the note-editing sidebar.
 * Supports 5 states (idle, updating, interrupted, completed, ignored),
 * 2 variants (rule, cpt), and 2 severity levels (non_compliant, warning).
 */

import React, { useState } from 'react';
import { BrandLoader } from '@components/primitives/BrandLoader';
import { MoreVert as MoreVertIcon, Undo as UndoIcon } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { motion } from 'motion/react';

import { AuditCardExpandedContent } from './AuditCardExpandedContent';
import type { AuditRuleCardProps } from './AuditRuleCard.types';
import {
  getCompletedBadgeConfig,
  getIgnoredBadgeConfig,
  getStatusConfig,
} from './AuditRuleCard.types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Height (px) of the expandable area when collapsed.
 *  Fits: title + description/source + ~2 lines of suggestion. */
const COLLAPSED_CONTENT_HEIGHT = 46;

/** Max lines for change summary when not undoable. */
const SUMMARY_CLAMP_LINES = 2;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusBadge({
  text,
  bgColor,
  textColor,
}: {
  text: string;
  bgColor: string;
  textColor: string;
}) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: bgColor,
        color: textColor,
        fontSize: '0.65rem',
        fontWeight: 600,
        px: 1,
        py: 0.25,
        borderRadius: '12px',
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
      }}
    >
      {text}
    </Box>
  );
}

function CptComparison({
  currentCode,
  suggestedCode,
}: {
  currentCode?: string | null | undefined;
  suggestedCode?: string | null | undefined;
}) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {currentCode && (
        <Typography
          component="span"
          sx={{
            fontSize: '0.8rem',
            fontWeight: 500,
            color: 'textColors.lightText',
            textDecoration: 'line-through',
          }}
        >
          {currentCode}
        </Typography>
      )}
      <Typography component="span" sx={{ fontSize: '0.75rem', color: 'textColors.lightText' }}>
        →
      </Typography>
      {suggestedCode && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: '#FCEBEB',
            color: '#791F1F',
            fontSize: '0.75rem',
            fontWeight: 600,
            px: 1,
            py: 0.25,
            borderRadius: '12px',
            whiteSpace: 'nowrap',
            lineHeight: 1.4,
          }}
        >
          ✦ {suggestedCode}
        </Box>
      )}
    </Box>
  );
}

function OverflowMenu({
  isAdmin,
  onEditRule,
  onViewAnalytics,
  onDisableRule,
  onLearnMore,
  onMarkResolved,
}: {
  isAdmin?: boolean;
  onEditRule?: () => void;
  onViewAnalytics?: () => void;
  onDisableRule?: () => void;
  onLearnMore?: () => void;
  onMarkResolved?: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const hasItems = isAdmin || onLearnMore || onMarkResolved;
  if (!hasItems) return null;

  const hasAdminItems = isAdmin && (onEditRule || onViewAnalytics || onDisableRule);

  return (
    <>
      <IconButton
        size="small"
        onClick={e => setAnchorEl(e.currentTarget)}
        aria-label="More options"
        sx={{ color: 'textColors.lightText', p: 0.5 }}
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {onLearnMore && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onLearnMore();
            }}
          >
            Learn more
          </MenuItem>
        )}
        {onMarkResolved && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onMarkResolved();
            }}
          >
            Mark as resolved
          </MenuItem>
        )}
        {(onLearnMore || onMarkResolved) && hasAdminItems && <Divider />}
        {isAdmin && onEditRule && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onEditRule();
            }}
          >
            Edit rule
          </MenuItem>
        )}
        {isAdmin && onViewAnalytics && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onViewAnalytics();
            }}
          >
            View analytics
          </MenuItem>
        )}
        {isAdmin && onDisableRule && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onDisableRule();
            }}
          >
            Disable rule
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const AuditRuleCard: React.FC<AuditRuleCardProps> = ({
  data,
  status,
  completedVia,
  isAdmin,
  onApprove,
  disableApprove,
  onIgnore,
  onUndo,
  onMarkResolved,
  onEditRule,
  onViewAnalytics,
  onDisableRule,
  onLearnMore,
  onSectionClick,
  expandMode = 'inline',
  onExpandModal,
  interruptContent,
  onCancelFix,
  changeSummary,
  canUndo,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasBeenExpanded, setHasBeenExpanded] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const useModalExpand = expandMode === 'modal' && !!onExpandModal;

  const config = getStatusConfig(data.variant, data.severity, data.suggestedCptCode);
  const isIdle = status === 'idle';
  const isUpdating = status === 'updating';
  const isInterrupted = status === 'interrupted';
  const isCompleted = status === 'completed';
  const isIgnored = status === 'ignored';
  const isCollapsed = isCompleted || isIgnored;

  // Determine badge props based on state
  const badgeProps = (() => {
    if (isCompleted && completedVia) {
      const cfg = getCompletedBadgeConfig(completedVia);
      return { text: cfg.text, bgColor: cfg.bgColor, textColor: cfg.textColor };
    }
    if (isIgnored) {
      const cfg = getIgnoredBadgeConfig();
      return { text: cfg.text, bgColor: cfg.bgColor, textColor: cfg.textColor };
    }
    return {
      text: config.badgeText,
      bgColor: config.badgeBgColor,
      textColor: config.badgeTextColor,
    };
  })();

  // Source line: "Document Title · 2025-10-15"
  const sourceLine = [data.source?.documentTitle, data.source?.documentDate]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: isInterrupted ? '2px solid' : '1px solid',
        borderColor: isInterrupted ? '#EF9F27' : 'borderColors.lightprimary',
        bgcolor: isCollapsed ? 'grey.50' : 'background.paper',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: isInterrupted ? '#EF9F27' : 'borderColors.brand',
          boxShadow: isCollapsed ? 0 : 1,
        },
      }}
    >
      {/* ── Header row: badge left, undo + overflow right ── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        {data.variant === 'cpt' && !isCollapsed ? (
          <CptComparison currentCode={data.currentCptCode} suggestedCode={data.suggestedCptCode} />
        ) : (
          <StatusBadge {...badgeProps} />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {isCollapsed && onUndo && canUndo && (
            <IconButton
              size="small"
              onClick={onUndo}
              aria-label="Undo"
              sx={{ color: 'textColors.lightText', p: 0.5 }}
            >
              <UndoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <OverflowMenu
            isAdmin={isAdmin}
            onEditRule={onEditRule}
            onViewAnalytics={onViewAnalytics}
            onDisableRule={onDisableRule}
            onLearnMore={onLearnMore}
            onMarkResolved={isIdle ? onMarkResolved : undefined}
          />
        </Box>
      </Box>

      {/* ── Completed state ── */}
      {isCompleted && (
        <>
          <Typography
            variant="body2"
            sx={{ color: 'textColors.lightText', mb: changeSummary ? 1 : 0 }}
          >
            {data.ruleTitle}
          </Typography>

          {/* Change summary */}
          {changeSummary &&
            (canUndo ? (
              /* Undoable: full summary text */
              <Typography
                variant="caption"
                sx={{ color: 'textColors.lightText', lineHeight: 1.6, display: 'block' }}
              >
                {changeSummary}
              </Typography>
            ) : (
              /* Not undoable: truncated with gradient + hover hint, expandable */
              <Box
                sx={{
                  position: 'relative',
                  cursor: isSummaryExpanded ? 'default' : 'pointer',
                  '&:hover .summary-show-more': isSummaryExpanded ? {} : { opacity: 1 },
                }}
                onClick={() => {
                  if (!isSummaryExpanded) {
                    setIsSummaryExpanded(true);
                  }
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'textColors.lightText',
                    lineHeight: 1.6,
                    display: '-webkit-box',
                    ...(!isSummaryExpanded && {
                      WebkitLineClamp: SUMMARY_CLAMP_LINES,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }),
                  }}
                >
                  {changeSummary}
                </Typography>
                {!isSummaryExpanded && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 24,
                      background: 'linear-gradient(rgba(249,250,251,0), rgba(249,250,251,1))',
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      className="summary-show-more"
                      variant="caption"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      Show more
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          {/* Collapse trigger for expanded summary */}
          {changeSummary && !canUndo && isSummaryExpanded && (
            <Box
              onClick={() => setIsSummaryExpanded(false)}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 0.5,
                cursor: 'pointer',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Show less
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* ── Ignored state ── */}
      {isIgnored && (
        <Typography variant="body2" sx={{ color: 'textColors.lightText' }}>
          {data.ruleTitle}
        </Typography>
      )}

      {/* ── Interrupted state — AI needs user input ── */}
      {isInterrupted && interruptContent && (
        <>
          {/* Rule title for context */}
          <Typography variant="body2" sx={{ color: 'textColors.lightText', mb: 1.5 }}>
            {data.ruleTitle}
          </Typography>

          {/* Amber interrupt block */}
          <Box
            sx={{
              bgcolor: '#FAEEDA',
              borderRadius: 1.5,
              p: 1.5,
              mb: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                fontSize: '0.6875rem',
                color: '#854F0B',
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                display: 'block',
                mb: 1,
              }}
            >
              Input needed
            </Typography>
            {interruptContent}
          </Box>

          {/* Cancel fix */}
          {onCancelFix && (
            <Button
              variant="text"
              size="small"
              onClick={() => onCancelFix()}
              sx={{
                color: 'textColors.lightText',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                px: 1,
              }}
            >
              Cancel fix
            </Button>
          )}
        </>
      )}

      {/* ── Idle / Updating state ── */}
      {(isIdle || isUpdating) && (
        <>
          {/* Expandable content area — title, description, suggestion all inside */}
          <Box
            sx={{
              position: 'relative',
              mb: 1.5,
              cursor: isExpanded && !useModalExpand ? 'default' : useModalExpand ? 'pointer' : 'pointer',
              '&:hover .show-more-text': isExpanded && !useModalExpand ? {} : { opacity: 1 },
              // Modal mode: gradient only visible on hover
              ...(useModalExpand && {
                '&:hover .modal-gradient': { opacity: 1 },
              }),
            }}
            onClick={() => {
              if (useModalExpand && onExpandModal) {
                onExpandModal();
              } else if (!isExpanded) {
                setIsExpanded(true);
                setHasBeenExpanded(true);
              }
            }}
          >
            <motion.div
              animate={{
                height: useModalExpand
                  ? 'auto' // Modal mode: content dictates height
                  : isExpanded
                    ? 'auto'
                    : COLLAPSED_CONTENT_HEIGHT,
              }}
              initial={false}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              style={{ overflow: useModalExpand ? 'visible' : 'hidden' }}
            >
              {/* Title */}
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: 'textColors.darkText', mb: 0.25 }}
              >
                {data.ruleTitle}
              </Typography>

              {/* CPT description (replaces source line position on CPT cards) */}
              {data.variant === 'cpt' && data.cptDescription && (
                <Typography
                  variant="caption"
                  sx={{ color: 'textColors.lightText', display: 'block', lineHeight: 1.5, mb: 0.5 }}
                >
                  {data.cptDescription}
                </Typography>
              )}

              {/* Source info — rule cards only (wraps, no ellipsis) */}
              {data.variant === 'rule' && sourceLine && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'textColors.lightText',
                    display: 'block',
                    lineHeight: 1.5,
                    mb: 0.75,
                  }}
                >
                  {sourceLine}
                </Typography>
              )}

              {/* Suggestion text */}
              {data.suggestion && (
                <Typography
                  variant="caption"
                  sx={{ color: 'textColors.lightText', lineHeight: 1.6 }}
                >
                  {data.suggestion}
                </Typography>
              )}

              {/* Expanded detail content — stays mounted after first expand so collapse animates smoothly.
                  Not rendered in modal mode — detail shown in RuleDetailModal instead. */}
              {!useModalExpand && (isExpanded || hasBeenExpanded) && (
                <AuditCardExpandedContent data={data} onSectionClick={onSectionClick} />
              )}
            </motion.div>

            {/* Gradient fade + hint text */}
            {(!isExpanded || useModalExpand) && (
              <Box
                className={useModalExpand ? 'modal-gradient' : undefined}
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 48,
                  background: 'linear-gradient(rgba(255,255,255,0), rgba(255,255,255,1))',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  pb: 0.5,
                  // Modal mode: gradient hidden by default, shown on hover via parent
                  ...(useModalExpand && {
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  }),
                }}
              >
                <Typography
                  className="show-more-text"
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {useModalExpand ? 'View details' : 'Show more'}
                </Typography>
              </Box>
            )}

            {/* Collapse trigger when expanded (inline mode only) */}
            {isExpanded && !useModalExpand && (
              <Box
                onClick={() => setIsExpanded(false)}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  pt: 1,
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Show less
                </Typography>
              </Box>
            )}
          </Box>

          {/* ── Action bar ── */}
          {isUpdating ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
              <BrandLoader size="sm" label="Updating note..." />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="text"
                size="medium"
                onClick={onIgnore}
                sx={{
                  color: 'textColors.lightText',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  px: 1.5,
                }}
              >
                {config.secondaryLabel}
              </Button>
              <Button
                variant={config.buttonVariant}
                color="primary"
                size="medium"
                onClick={onApprove}
                disabled={disableApprove}
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                }}
              >
                {config.buttonLabel}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
